import React, { useState, useMemo } from 'react';
import {
  WidgetFilterChips,
  DrillDownGrid,
  WidgetChart,
  WidgetTable,
} from '@t-one-internal/design-system';

import { MultiKPI } from '@t-one-internal/design-system/lib/components/widgets/my-portal/MultiKPI';
import { GoalList } from '@t-one-internal/design-system/lib/components/widgets/my-portal/GoalList';
import type { ShareDestination } from '@t-one-internal/design-system';
import { WidgetModal } from './WidgetModal';
import { PersistentControls } from './PersistentControls';
import { WidgetHeader } from './WidgetHeader';
import type { ChartPointClickContext } from '@t-one-internal/design-system/lib/components/widgets/WidgetChart';
import type { QueryFilter, WidgetFilterConfig, WidgetCapabilities, ChartType } from '../../types/analytics';
import type { DrillToDetailContext } from '../../types/drillToDetail';
import { resolveMetricKey, getDataFieldConfig } from '../../config/widgetRegistry';
import { useWidgetDataSource } from '../../hooks/useWidgetDataSource';
import {
  TEMPLATE_RENDERERS,
  LEARN_METRIC_RENDERERS,
  CHART_TYPE_RENDERERS,
} from './widgetContentMap';

/**
 * Convert a field key (CamelCase, snake_case, or mixed) to a human-readable label.
 * Examples:
 *   "AvgProgress"          → "Avg Progress"
 *   "total_enrolled"       → "Total Enrolled"
 *   "SessionCompletionRate"→ "Session Completion Rate"
 *   "TotalTimeHours"       → "Total Time Hours"
 *   "UtilizationPct"       → "Utilization Pct"
 */
const formatFieldLabel = (key: string): string => {
  return key
    // Insert space before each uppercase letter that follows a lowercase letter or digit
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    // Insert space before sequences like "ILT" followed by a lowercase letter
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    // Replace underscores with spaces
    .replace(/_/g, ' ')
    // Capitalize the first letter of each word
    .replace(/\b\w/g, (c: string) => c.toUpperCase())
    .trim();
};

interface WidgetWrapperProps {
  /** Widget ID */
  widgetId: string | number;
  /** Widget title */
  title: string;
  /** Widget content - the actual chart/data component */
  children?: React.ReactNode;
  /** Chart type for modal view */
  chartType?: 'bar' | 'line' | 'area' | 'pie' | string;
  /** Chart data for modal view */
  chartData?: any;
  /** Drill-down grid data */
  drillDownData?: Array<{
    id: string;
    [key: string]: any;
  }>;
  /** Current filters */
  filters?: QueryFilter[];
  /** Available filters from template */
  availableFilters?: WidgetFilterConfig[];
  /** Field labels for filters */
  fieldLabels?: Record<string, string>;
  /** Callback when filters change */
  onFiltersChange?: (filters: QueryFilter[]) => Promise<void>;
  /** Callback when widget is removed */
  onRemove?: (id: string | number) => void;
  /** Callback when widget is cloned */
  onClone?: (id: string | number) => void;
  /** Callback when widget is sent to another dashboard */
  onSendToDashboard?: (id: string | number, targetDashboardId: number) => void;
  /** Callback to fetch share destinations for this widget */
  onFetchShareDestinations?: (widgetId: string | number) => Promise<ShareDestination[]>;
  /** Widget capabilities (for chart type options) */
  capabilities?: WidgetCapabilities | null;
  /** Current chart type */
  currentChartType?: ChartType;
  /** Callback when chart type changes */
  onChartTypeChange?: (chartType: ChartType) => void;
  /** Callback when title changes */
  onTitleChange?: (newTitle: string) => Promise<void>;
  /** Widget Template Key (for specific rendering) */
  templateKey?: string;
  /** Metric Key (for data source detection) */
  metricKey?: string;
  /** Query Config (for data source detection) */
  queryConfig?: any;
  /** Visualization Config (for options) */
  visualizationConfig?: any;
  /** Owner user ID for shared dashboard context - when viewing another user's dashboard */
  ownerUserId?: number | string;
  /** Read-only mode - hides header controls (drag handle, filter, expand, menu) */
  readOnly?: boolean;
  /** Template scoping value (fixed at creation time) */
  scoping?: string;
  /** Whether to show the filter icon (default: true) */
  showFilterIcon?: boolean;
  /** Whether to show the table/grid view toggle (default: true) */
  showTableView?: boolean;
  /** Externally provided data — skips internal useAnalyticsData fetch when set */
  externalData?: { data: any; loading: boolean; error: string | null };
  /** Public share context — passed into drill-to-detail so it uses public API */
  publicContext?: { ownerUserId: string; accountId: string; shareToken: string };
  /** Callback for drill-to-detail — opens detail modal when chart point or KPI is clicked */
  onDrillToDetail?: (context: DrillToDetailContext) => void;
}

/**
 * Widget Wrapper Component
 *
 * Provides the standard widget UI with:
 * - Drag handle (top-left corner only)
 * - Filter selector and chips
 * - Expand button (opens modal)
 * - Details button (toggles chart/grid view)
 * - Three-dot menu (Clone, Send to Dashboard)
 */
export const WidgetWrapper: React.FC<WidgetWrapperProps> = ({
  widgetId,
  title,
  children,
  chartType,
  chartData,
  drillDownData,
  filters = [],
  availableFilters = [],
  fieldLabels = {},
  onFiltersChange,
  onRemove,
  onClone,
  onSendToDashboard,
  onFetchShareDestinations,
  capabilities,
  currentChartType,
  onChartTypeChange,
  onTitleChange,
  templateKey,
  metricKey,
  queryConfig,
  visualizationConfig,
  ownerUserId,
  readOnly,
  scoping,
  showFilterIcon = true,
  showTableView = true,
  externalData,
  publicContext,
  onDrillToDetail,
}) => {
  const [showDrillDown, setShowDrillDown] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [initialFilterOpen, setInitialFilterOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Separate mandatory and non-mandatory filters
  const { mandatoryFilters, nonMandatoryFilters } = useMemo(() => {
    const mandatory = availableFilters.filter((f: any) => f.required === true);
    const nonMandatory = availableFilters.filter((f: any) => f.required !== true);
    return { mandatoryFilters: mandatory, nonMandatoryFilters: nonMandatory };
  }, [availableFilters]);

  const showPersistentControls = mandatoryFilters.length > 0;

  // Resolve metricKey using centralized widget registry
  const resolvedMetricKey = resolveMetricKey({
    metricKey,
    templateKey,
    queryConfig: queryConfig as Record<string, unknown>,
    visualizationConfig: visualizationConfig as Record<string, unknown>,
  });

  // Get data field configuration for chart rendering
  const dataFieldConfig = getDataFieldConfig({ templateKey, metricKey: resolvedMetricKey });

  // Debug logging for widget data resolution
  if (process.env.NODE_ENV === 'development') {
    console.log('[WidgetWrapper] Data resolution:', {
      widgetId,
      title,
      templateKey,
      metricKeyProp: metricKey,
      resolvedMetricKey,
      dataFieldConfig,
      queryConfig,
      chartType,
    });
  }

  // ── Data fetching via orchestrator hook ────────────────────────────────────
  const {
    data: widgetData,
    loading,
    error,
    metadata: analyticsMetadata,
    dataSource,
    rbColumns,
  } = useWidgetDataSource({
    templateKey,
    metricKey: resolvedMetricKey,
    chartType,
    queryConfig,
    filters,
    ownerUserId,
    scoping,
    externalData,
  });

  // Aliases for backward compatibility with renderContent logic below
  const isReportBuilderWidget = dataSource === 'report_builder';
  const rbData = isReportBuilderWidget ? widgetData : null;
  const rbLoading = isReportBuilderWidget ? loading : false;
  const rbError = isReportBuilderWidget ? error : null;

  const reviewCloudData = dataSource === 'review_cloud' ? widgetData : null;

  const analyticsData = dataSource === 'analytics' ? widgetData : null;
  const analyticsLoading = dataSource === 'analytics' ? loading : false;
  const analyticsError = dataSource === 'analytics' ? error : null;

  const reportBuilderId = queryConfig?.report_builder_id as string | undefined;

  const filterCount = filters.length;
  // Show filter icon based on prop - some pages (like My Portal) may not need filters
  const hasFiltersAvailable = showFilterIcon;

  const handleRemoveFilter = async (field: string) => {
    if (onFiltersChange) {
      const updatedFilters = filters.filter(f => f.field !== field);
      await onFiltersChange(updatedFilters);
    }
  };

  const handleClone = () => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[WidgetWrapper] handleClone called, widgetId:', widgetId, 'onClone exists:', !!onClone);
    }
    if (onClone) {
      onClone(widgetId);
    }
  };

  const handleSendToDashboard = (wId: string, targetDashboardId: number) => {
    if (onSendToDashboard) {
      onSendToDashboard(widgetId, targetDashboardId);
    }
  };

  const handleFetchShareDestinations = async (wId: string): Promise<ShareDestination[]> => {
    if (onFetchShareDestinations) {
      return onFetchShareDestinations(wId);
    }
    return [];
  };

  // Handle chart point click for drill-to-detail (template widgets)
  const handleChartPointClick = (context: ChartPointClickContext) => {
    if (!onDrillToDetail || !resolvedMetricKey) return;

    const clickFilter: QueryFilter = {
      field: context.originalData?.field_name as string || 'name',
      operator: '=',
      value: context.name,
    };

    onDrillToDetail({
      metricKey: resolvedMetricKey,
      widgetTitle: title,
      clickFilter,
      existingFilters: filters,
      segmentLabel: context.name,
      segmentValue: context.value,
      scoping,
      publicContext,
    });
  };

  // Handle chart point click for drill-to-detail (report builder widgets)
  const handleRbChartPointClick = (context: ChartPointClickContext) => {
    if (!onDrillToDetail || !reportBuilderId) return;

    onDrillToDetail({
      metricKey: '',
      widgetTitle: title,
      segmentLabel: context.name,
      segmentValue: context.value,
      scoping,
      reportBuilderId,
    });
  };

  // ── Column normalizer ─────────────────────────────────────────────────────
  // analyticsMetadata.columns are ColumnSchema {key, label, type} but WidgetTable
  // expects TableColumn {name, label, data_type}. Normalize before passing.
  const normalizeToTableColumns = (cols: any[] | undefined) => {
    if (!cols) return undefined;
    return cols.map((c: any) => ({
      name: c.key ?? c.name ?? '',
      label: c.label ?? '',
      data_type: c.type ?? c.data_type,
      sortable: c.sortable !== false,
    }));
  };

  // ── renderContent ──────────────────────────────────────────────────────────
  const renderContent = (): React.ReactNode => {
    // ── 1. Report Builder widgets ───────────────────────────────────────────
    if (isReportBuilderWidget) {
      if (rbLoading) return <div className="flex items-center justify-center h-full text-gray-400">Loading...</div>;
      if (rbError) return <div className="flex items-center justify-center h-full text-red-400">{rbError}</div>;
      if (!rbData) return <div className="flex items-center justify-center h-full text-gray-400">No data</div>;

      const effectiveChartType = chartType || 'table';
      const tableColumns = rbColumns.map(c => ({ name: c.key, label: c.label, data_type: c.data_type, sortable: c.sortable }));

      if (effectiveChartType === 'table') {
        return <WidgetTable data={rbData} columns={tableColumns} />;
      }

      const normalizedData = Array.isArray(rbData) ? rbData : [];
      const dimensionCol = rbColumns.find(c => c.is_dimension);
      const measureCol = rbColumns.find(c => c.is_measure);
      const rbNameField = dimensionCol?.key;
      const rbValueField = measureCol?.key;
      const rbDataFields = (rbNameField || rbValueField)
        ? { nameField: rbNameField, valueField: rbValueField }
        : undefined;
      const rbChartConfig = {
        ...(visualizationConfig as any),
        keys: {
          ...(visualizationConfig as any)?.keys,
          ...(rbNameField ? { name: rbNameField } : {}),
          ...(rbValueField ? { value: rbValueField } : {}),
        },
        labels: {
          ...(dimensionCol ? { name: dimensionCol.label } : {}),
          ...(measureCol ? { value: measureCol.label } : {}),
        },
      };

      return (
        <WidgetChart
          type={effectiveChartType as any}
          data={normalizedData}
          config={rbChartConfig}
          dataFields={rbDataFields}
          onPointClick={onDrillToDetail ? handleRbChartPointClick : undefined}
        />
      );
    }

    // Shared loading / error states for all remaining sources
    if (loading) return <div className="flex items-center justify-center h-full text-gray-400">Loading...</div>;
    if (error) return <div className="flex items-center justify-center h-full text-red-400">{error}</div>;

    // ── 2. Template renderer lookup ─────────────────────────────────────────
    if (templateKey) {
      const TemplateRenderer = TEMPLATE_RENDERERS[templateKey];
      if (TemplateRenderer) {
        return (
          <TemplateRenderer
            data={widgetData}
            loading={loading}
            error={error}
            templateKey={templateKey}
            chartType={chartType}
            visualizationConfig={visualizationConfig}
            queryConfig={queryConfig}
            resolvedMetricKey={resolvedMetricKey}
            onPointClick={onDrillToDetail ? handleChartPointClick : undefined}
          />
        );
      }
    }

    // ── 3. Learn metric renderer lookup ────────────────────────────────────
    if (resolvedMetricKey) {
      const LearnRenderer = LEARN_METRIC_RENDERERS[resolvedMetricKey];
      if (LearnRenderer) {
        return (
          <LearnRenderer
            data={widgetData}
            loading={loading}
            error={error}
            templateKey={templateKey}
            chartType={chartType}
            visualizationConfig={visualizationConfig}
            queryConfig={queryConfig}
            resolvedMetricKey={resolvedMetricKey}
          />
        );
      }
    }

    // ── 4. Learn_ metric — legacy inline rendering path ────────────────────
    // Handles KPI cards, multi-stat, time-series, tables, charts derived from
    // analytics data for learn_ metric keys that don't have a dedicated
    // LEARN_METRIC_RENDERERS entry (e.g. templateKey-specific KPI overrides).
    const dataToRender = analyticsData || chartData;

    if (resolvedMetricKey?.startsWith('learn_') && analyticsError && !analyticsLoading) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-red-400 py-8">
          <svg className="w-8 h-8 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
          <span className="text-sm text-red-500">Unable to load data</span>
          <span className="text-xs text-gray-400 mt-1">{analyticsError}</span>
        </div>
      );
    }

    if (resolvedMetricKey?.startsWith('learn_') && !dataToRender && !analyticsLoading) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-gray-400 py-8">
          <svg className="w-10 h-10 mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
          <span className="text-sm">No data available</span>
        </div>
      );
    }

    if (resolvedMetricKey?.startsWith('learn_') && dataToRender) {
      const learnRow = Array.isArray(dataToRender) ? dataToRender[0] : dataToRender;
      const learnRows = Array.isArray(dataToRender) ? dataToRender : [dataToRender];

      // KPI card: extract the right field based on template key
      if (chartType === 'kpi_card' || chartType === 'gauge') {
        let value: number | string = '—';
        let subtitle = '';

        if (learnRow) {
          switch (templateKey) {
            case 'learn_overview_enrolled':
              value = learnRow.totalEnrolled ?? learnRow.total_enrolled ?? learnRow.count ?? 0;
              break;
            case 'learn_overview_in_progress':
              value = learnRow.totalInProgress ?? learnRow.total_in_progress ?? 0;
              break;
            case 'learn_overview_completed':
              value = learnRow.totalCompleted ?? learnRow.total_completed ?? 0;
              subtitle = (learnRow.completionRate ?? learnRow.completion_rate) ? `${learnRow.completionRate ?? learnRow.completion_rate}% completion rate` : '';
              break;
            case 'learn_overview_overdue':
              value = learnRow.overdueCount ?? learnRow.overdue_count ?? 0;
              break;
            case 'learn_mgr_team_time':
              value = `${learnRow.totalHours ?? learnRow.total_hours ?? 0}h`;
              subtitle = `${learnRow.avgHoursPerPerson ?? learnRow.avg_hours_per_person ?? 0}h avg per person`;
              break;
            case 'learn_mgr_mandatory_compliance':
              value = `${learnRow.complianceRate ?? learnRow.compliance_rate ?? 0}%`;
              subtitle = `${learnRow.completedMandatory ?? learnRow.completed_mandatory ?? 0} of ${learnRow.totalMandatory ?? learnRow.total_mandatory ?? 0} mandatory`;
              break;
            case 'learn_mgr_pending_approvals':
              value = learnRow.pendingCount ?? learnRow.pending_count ?? 0;
              break;
            case 'learn_coach_unread':
              value = learnRow.unreadCount ?? learnRow.unread_count ?? 0;
              break;
            case 'learn_org_kpi_summary': {
              // Multiple KPI card widgets share this metric — use visualization_config
              // or widget title to determine which field to show
              const fieldHint = (visualizationConfig as any)?.value_field;
              const lowerTitle = title.toLowerCase();
              if (fieldHint && learnRow[fieldHint] !== undefined) {
                value = learnRow[fieldHint] ?? 0;
              } else if (lowerTitle.includes('enrolled') || lowerTitle.includes('enrollment')) {
                value = learnRow.totalEnrolled ?? learnRow.total_enrolled ?? 0;
              } else if (lowerTitle.includes('in progress') || lowerTitle.includes('in-progress')) {
                value = learnRow.totalInProgress ?? learnRow.total_in_progress ?? 0;
              } else if (lowerTitle.includes('completed') || lowerTitle.includes('completion')) {
                value = learnRow.totalCompleted ?? learnRow.total_completed ?? 0;
                subtitle = (learnRow.completionRate ?? learnRow.completion_rate) ? `${learnRow.completionRate ?? learnRow.completion_rate}% completion rate` : '';
              } else if (lowerTitle.includes('learner')) {
                value = learnRow.totalLearners ?? learnRow.total_learners ?? 0;
              } else if (lowerTitle.includes('time') || lowerTitle.includes('hour')) {
                value = `${learnRow.totalTimeHours ?? learnRow.total_time_hours ?? 0}h`;
              } else if (lowerTitle.includes('progress')) {
                value = `${learnRow.avgProgress ?? learnRow.avg_progress ?? 0}%`;
              } else {
                value = Object.values(learnRow).find(v => typeof v === 'number') as number ?? '—';
              }
              break;
            }
            case 'learn_org_overdue':
              value = learnRow.overdueCount ?? learnRow.overdue_count ?? 0;
              break;
            // TMS KPI cards — all read from learn_tms_kpi_overview single-row result
            case 'learn_tms_total_sessions':
              value = learnRow.totalSessions ?? learnRow.total_sessions ?? 0;
              subtitle = 'Total ILT sessions';
              break;
            case 'learn_tms_attendance_rate':
              value = `${learnRow.attendanceRate ?? learnRow.attendance_rate ?? 0}%`;
              subtitle = 'Across all sessions';
              break;
            case 'learn_tms_completion_rate':
              value = `${learnRow.attendanceRate ?? learnRow.attendance_rate ?? 0}%`;
              subtitle = 'ILT completion rate';
              break;
            case 'learn_tms_avg_training_hours':
              value = `${learnRow.avgHours ?? learnRow.avg_hours ?? 0}h`;
              subtitle = 'Avg session duration';
              break;
            case 'learn_tms_sessions_per_trainer':
              value = learnRow.sessionsPerTrainer ?? learnRow.sessions_per_trainer ?? 0;
              subtitle = 'Avg per trainer';
              break;
            default:
              value = Object.values(learnRow).find(v => typeof v === 'number') as number ?? '—';
          }
        }

        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: '16px' }}>
            <div style={{ fontSize: '48px', fontWeight: 700, color: '#1e293b', lineHeight: 1.1 }}>
              {typeof value === 'number' ? value.toLocaleString() : value}
            </div>
            {subtitle && <div style={{ fontSize: '13px', color: '#64748b', marginTop: '6px' }}>{subtitle}</div>}
          </div>
        );
      }

      // Coaching empty state: when all numeric values are 0 for coaching metric keys,
      // show a contextual empty state instead of a blank multi_stat card.
      const isCoachingMetric =
        resolvedMetricKey?.startsWith('learn_engagement_') ||
        resolvedMetricKey?.startsWith('learn_coaching_') ||
        resolvedMetricKey?.startsWith('learn_coach_');
      if (isCoachingMetric && learnRow) {
        const numericValues = Object.values(learnRow).filter(
          (v): v is number => typeof v === 'number'
        );
        const allZero = numericValues.length > 0 && numericValues.every(v => v === 0);
        if (allZero) {
          return (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: '#9CA3AF',
                gap: 8,
              }}
            >
              <span style={{ fontSize: 14 }}>No coaching data available</span>
              <span style={{ fontSize: 12 }}>
                Data will appear once coaching engagements are created
              </span>
            </div>
          );
        }
      }

      // Multi-stat: transform single summary row into stats array
      if (chartType === 'multi_stat' && learnRow && !Array.isArray(learnRow.stats)) {
        // Use schema column labels from analytics metadata when available
        const colMap = new Map<string, string>();
        if (analyticsMetadata?.columns) {
          for (const col of analyticsMetadata.columns) {
            colMap.set(col.key || col.name, col.label);
          }
        }

        const stats = Object.entries(learnRow)
          .filter(([_, v]) => typeof v === 'number' || (typeof v === 'string' && !isNaN(Number(v))))
          .map(([key, val]) => ({
            label: colMap.get(key) || formatFieldLabel(key),
            value: typeof val === 'number' ? val : Number(val),
          }));
        return <MultiKPI data={stats} options={visualizationConfig?.options} />;
      }

      // Line/area chart: transform weekly/monthly trend data
      if ((chartType === 'line' || chartType === 'area' || chartType === 'bar') && learnRows.length > 1) {
        const timeField = learnRow?.week ? 'week' : learnRow?.month ? 'month' : learnRow?.date ? 'date' : null;
        if (timeField) {
          const trendData = learnRows.map((row: any) => ({
            name: new Date(row[timeField]).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            ...Object.fromEntries(Object.entries(row).filter(([k]) => k !== timeField)),
          }));
          return (
            <WidgetChart
              type={chartType as any}
              data={trendData}
              config={visualizationConfig as any}
              dataFields={dataFieldConfig}
              onPointClick={onDrillToDetail ? handleChartPointClick : undefined}
            />
          );
        }
      }

      // Table: pass through with auto-columns if no columns metadata
      if (chartType === 'table' && learnRows.length > 0) {
        const columns = normalizeToTableColumns(analyticsMetadata?.columns) || Object.keys(learnRows[0]).map((key: string) => ({
          name: key,
          label: formatFieldLabel(key),
          data_type: typeof learnRows[0][key] === 'number' ? 'number' : 'string',
          sortable: true,
        }));
        return <WidgetTable data={learnRows} columns={columns} />;
      }

      // Bar/donut/pie for categorized data
      if ((chartType === 'bar' || chartType === 'donut' || chartType === 'pie') && learnRows.length >= 1) {
        // Auto-detect name/value fields when registry doesn't have dataFields
        const fields = dataFieldConfig || (() => {
          const keys = Object.keys(learnRow || {});
          const stringField = keys.find(k => typeof learnRow[k] === 'string' && k !== 'id' && !k.endsWith('_id'));
          const numberField = keys.find(k => typeof learnRow[k] === 'number' || (typeof learnRow[k] === 'string' && !isNaN(Number(learnRow[k])) && k !== stringField && !k.endsWith('_id')));
          return stringField && numberField ? { nameField: stringField, valueField: numberField } : undefined;
        })();
        return (
          <WidgetChart
            type={chartType as any}
            data={learnRows}
            config={visualizationConfig as any}
            dataFields={fields}
            onPointClick={onDrillToDetail ? handleChartPointClick : undefined}
          />
        );
      }
    }

    // ── 5. Children passthrough ─────────────────────────────────────────────
    if (children) return <>{children}</>;

    // ── 6. Special override for My Goals widget ─────────────────────────────
    const resolvedDataToRender = dataToRender || widgetData;
    if (
      title === 'My Goals' ||
      templateKey === 'mp_goals_list' ||
      (visualizationConfig as any)?.displayType === 'goal_list'
    ) {
      return <GoalList data={resolvedDataToRender} options={visualizationConfig?.options} />;
    }

    // ── 7. Chart type renderer lookup ───────────────────────────────────────
    if (chartType) {
      // table needs analyticsMetadata context — handle inline before map lookup
      if (chartType === 'table') {
        return <WidgetTable data={resolvedDataToRender} columns={normalizeToTableColumns(analyticsMetadata?.columns)} />;
      }

      const ChartRenderer = CHART_TYPE_RENDERERS[chartType];
      if (ChartRenderer) {
        return (
          <ChartRenderer
            data={resolvedDataToRender}
            loading={loading}
            error={error}
            templateKey={templateKey}
            chartType={chartType}
            visualizationConfig={visualizationConfig}
            queryConfig={queryConfig}
            resolvedMetricKey={resolvedMetricKey}
            onPointClick={onDrillToDetail ? handleChartPointClick : undefined}
          />
        );
      }
    }

    // ── 8. KPI card (inline — needs drill-to-detail callback closure) ───────
    if (chartType === 'kpi_card') {
      const kpiData = Array.isArray(resolvedDataToRender) ? resolvedDataToRender[0] : resolvedDataToRender;
      const value = typeof kpiData === 'number' ? kpiData : (kpiData?.value ?? kpiData?.count ?? '—');
      const label = kpiData?.label || (visualizationConfig as any)?.label || '';

      const handleKpiClick = () => {
        if (onDrillToDetail && resolvedMetricKey) {
          onDrillToDetail({
            metricKey: resolvedMetricKey,
            widgetTitle: title,
            existingFilters: filters,
            segmentLabel: label || title,
            segmentValue: typeof value === 'number' ? value : undefined,
            scoping,
            publicContext,
          });
        }
      };

      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            padding: '16px',
          }}
          onClick={onDrillToDetail ? handleKpiClick : undefined}
          role={onDrillToDetail ? 'button' : undefined}
          tabIndex={onDrillToDetail ? 0 : undefined}
        >
          <div style={{ fontSize: '48px', fontWeight: '700', color: '#1f2937', lineHeight: '1' }}>
            {typeof value === 'number' ? value.toLocaleString() : value}
          </div>
          {label && (
            <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '8px' }}>
              {label}
            </div>
          )}
        </div>
      );
    }

    // ── 9. Generic chart fallback ───────────────────────────────────────────
    if (chartType && resolvedDataToRender) {
      // Normalize camelCase keys → snake_case aliases for chart field mapping
      const normalizedChartData = Array.isArray(resolvedDataToRender)
        ? resolvedDataToRender.map(item => {
            const normalized: Record<string, unknown> = {};
            for (const [key, val] of Object.entries(item as Record<string, unknown>)) {
              normalized[key] = val;
              const snakeKey = key.replace(/[A-Z]/g, l => `_${l.toLowerCase()}`);
              if (snakeKey !== key) {
                normalized[snakeKey] = val;
              }
            }
            return normalized;
          })
        : resolvedDataToRender;
      return (
        <WidgetChart
          type={chartType as any}
          data={normalizedChartData}
          config={visualizationConfig}
          dataFields={dataFieldConfig}
        />
      );
    }

    return null;
  };

  return (
    <>
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          border: '1px solid #e5e7eb',
        }}
      >
        {/* Widget Header */}
        <WidgetHeader
          title={title}
          widgetId={widgetId}
          readOnly={readOnly}
          scoping={scoping}
          templateKey={templateKey}
          filterCount={filterCount}
          hasFiltersAvailable={hasFiltersAvailable}
          showFilters={showFilters}
          onToggleFilters={() => setShowFilters(!showFilters)}
          drillDownDataLength={drillDownData?.length ?? 0}
          showDrillDown={showDrillDown}
          onToggleDrillDown={() => setShowDrillDown(!showDrillDown)}
          onExpand={() => setShowModal(true)}
          isMenuOpen={isMenuOpen}
          onMenuOpenChange={setIsMenuOpen}
          onClone={onClone ? handleClone : undefined}
          onSendToDashboard={onSendToDashboard ? handleSendToDashboard : undefined}
          onFetchShareDestinations={onFetchShareDestinations ? handleFetchShareDestinations : undefined}
          onRemove={onRemove ? () => onRemove(widgetId) : undefined}
          capabilities={capabilities}
          currentChartType={currentChartType}
          onChartTypeChange={onChartTypeChange}
          onTitleChange={onTitleChange}
        />

        {/* Filter Section - Only shown when showFilters is true */}
        {showFilters && hasFiltersAvailable && (
          <div
            style={{
              padding: '8px 12px',
              borderBottom: '1px solid #f3f4f6',
              backgroundColor: '#ffffff',
            }}
          >
            {filterCount > 0 && (
              <WidgetFilterChips
                filters={filters}
                onRemoveFilter={handleRemoveFilter}
                fieldLabels={fieldLabels}
              />
            )}
            {availableFilters.length > 0 ? (
              <button
                onClick={() => {
                  setInitialFilterOpen(true);
                  setShowModal(true);
                }}
                style={{
                  marginTop: filterCount > 0 ? '8px' : '0',
                  padding: '6px 12px',
                  backgroundColor: '#eff6ff',
                  border: '1px solid #bfdbfe',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '12px',
                  fontWeight: '500',
                  color: '#1e40af',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#dbeafe';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#eff6ff';
                }}
              >
                <span>+</span>
                {filterCount > 0 ? 'Edit Filters' : 'Add Filter'}
              </button>
            ) : filterCount === 0 ? (
              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                No filters configured for this widget
              </div>
            ) : null}
          </div>
        )}

        {/* Persistent Controls Bar - Scope Selector and Mandatory Filters */}
        {showPersistentControls && (
          <PersistentControls
            filters={filters}
            mandatoryFilterConfigs={mandatoryFilters}
            onFiltersChange={onFiltersChange || (async () => {})}
            disabled={readOnly}
          />
        )}

        {/* Widget Content Area */}
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            padding: '0',
            minHeight: 0,
          }}
        >
          {showDrillDown && drillDownData ? (
            <DrillDownGrid data={drillDownData} />
          ) : (
            renderContent()
          )}
        </div>
      </div>

      {/* Expanded Modal */}
      {showModal && (
        <WidgetModal
          widgetId={widgetId}
          title={title}
          filters={filters}
          availableFilters={availableFilters}
          fieldLabels={fieldLabels}
          onClose={() => {
            setShowModal(false);
            setInitialFilterOpen(false);
          }}
          onFiltersChange={onFiltersChange}
          capabilities={capabilities}
          initialFilterOpen={initialFilterOpen}
          currentChartType={currentChartType}
          onChartTypeChange={onChartTypeChange}
          onTitleChange={onTitleChange}
          metricKey={resolvedMetricKey}
          timeRange={queryConfig?.time_range}
          showFilterIcon={showFilterIcon}
          showTableView={showTableView}
          scoping={scoping}
          publicContext={publicContext}
        >
          {/* Pass the widget content to display in the modal */}
          {renderContent()}
        </WidgetModal>
      )}
    </>
  );
};

export default WidgetWrapper;
